import { datePickerAnatomy } from '@ark-ui/react/date-picker';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const datePickerSlotRecipe = defineSlotRecipe({
  className: 'datePicker',
  slots: [...datePickerAnatomy.keys(), 'months'],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8',
    },
    control: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    months: {
      display: 'flex',
      gap: '24',
    },
    positioner: {
      position: 'absolute',
    },
    content: {
      backgroundColor: 'background/primary',
      borderRadius: '8',
      boxShadow: 'elevation/lg',
      minWidth: rem(312),
      paddingBlock: '24',
      paddingInline: '16',
      zIndex: 'select',
    },
    view: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16',
    },
    viewControl: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    table: {
      width: 'full',
      borderCollapse: 'collapse',
      borderSpacing: 0,
      tableLayout: 'fixed',
    },
    tableBody: {
      '& > tr:first-child > td': {
        paddingTop: '8',
      },
    },
    tableRow: {
      display: 'table-row',
    },
    tableHeader: {
      color: 'text/tertiary',
      textAlign: 'center',
      textStyle: 'body/md/regular',
      width: '40',
      minWidth: '40',
      maxWidth: '40',
    },
    tableCell: {
      textAlign: 'center',
      paddingBlock: '2',
    },
    tableCellTrigger: {
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
      width: '100%',
      height: '32',
      color: 'text/primary',
      cursor: 'pointer',
      textStyle: 'body/md/regular',

      '&:hover:not([data-selected])': {
        backgroundColor: 'interactive/moderate',
        borderRadius: '0.5rem',
      },

      '&[data-unavailable]': {
        borderRadius: 0,
        backgroundColor: 'interactive/disabled',
        color: 'text/disabled',
        cursor: 'default',
        '&:hover, &[data-outside-range]:hover': {
          borderRadius: 0,
          backgroundColor: 'interactive/disabled',
          color: 'text/disabled',
        },
      },

      '&[data-outside-range]': {
        color: 'text/tertiary',
      },

      '&[data-outside-range][data-unavailable]': {
        color: 'text/disabled',
      },

      '&[data-outside-range][data-in-range], &[data-outside-range][data-in-range]:hover': {
        backgroundColor: 'interactive/moderate',
        color: 'interactive/bold',
      },

      '&[data-outside-range][data-range-end]': {
        backgroundColor: 'interactive/highlight',
        borderRadius: '0 0.5rem 0.5rem 0',
        color: 'interactive/subtle',
      },

      '&[data-outside-range][data-hover-range-start]:hover': {
        borderRadius: '0.5rem 0 0 0.5rem',
        color: 'interactive/subtle',
        backgroundColor: 'interactive/highlight',
      },

      '&[data-outside-range][data-hover-range-end]:hover': {
        borderRadius: '0 0.5rem 0.5rem 0',
        color: 'interactive/subtle',
        backgroundColor: 'interactive/highlight',
      },

      '&[data-outside-range][data-range-start],&[data-outside-range][data-range-end]': {
        backgroundColor: 'interactive/highlight',
        color: 'interactive/subtle',
      },

      '&[data-outside-range][data-in-range][data-range-start]:hover': {
        backgroundColor: 'interactive/highlight',
        borderRadius: '0.5rem 0 0 0.5rem',
        color: 'interactive/minimal',
      },

      '&[data-outside-range][data-in-range][data-range-end]:hover': {
        backgroundColor: 'interactive/highlight',
        borderRadius: '0 0.5rem 0.5rem 0',
        color: 'interactive/minimal',
      },

      '&[data-outside-range]:hover': {
        backgroundColor: 'transparent',
        color: 'text/tertiary',
      },

      '&[data-in-range]': {
        backgroundColor: 'interactive/moderate',
        color: 'text/primary',
      },

      '&[data-in-range]:hover': {
        backgroundColor: 'interactive/muted',
        borderRadius: 0,
      },

      '&[data-selected]': {
        backgroundColor: 'interactive/base',
        color: 'text/on-color',
        borderRadius: '0.5rem',
      },

      '&[data-selected]:hover': {
        backgroundColor: 'interactive/base',
        borderRadius: '0.5rem',
        color: 'interactive/moderate',
      },

      '&[data-range-start], &[data-hover-range-start], &[data-hover-range-start]:hover': {
        backgroundColor: 'interactive/base',
        borderRadius: '0.5rem 0 0 0.5rem',
        color: 'text/on-color',
      },

      '&[data-range-end], &[data-hover-range-end], &[data-hover-range-end]:hover': {
        backgroundColor: 'interactive/base',
        borderRadius: '0 0.5rem 0.5rem 0',
        color: 'text/on-color',
      },

      '&[data-range-start]:hover': {
        backgroundColor: 'interactive/base',
        borderRadius: '0.5rem 0 0 0.5rem',
        color: 'interactive/moderate',
      },

      '&[data-range-end]:hover': {
        backgroundColor: 'interactive/base',
        borderRadius: '0 0.5rem 0.5rem 0',
        color: 'interactive/moderate',
      },

      '&[data-range-start][data-range-end]:hover': {
        backgroundColor: 'interactive/base',
        color: 'interactive/moderate',
      },

      '&[data-hover-range-start][data-hover-range-end]': {
        borderRadius: '0.5rem',
      },

      '&[data-today] > span': {
        border: '1px solid',
        borderColor: 'interactive/base',
      },

      '&[data-today][data-selected] > span': {
        border: '1px solid',
        borderColor: 'interactive/moderate',
      },

      '&[data-today][data-range-end] > span, &[data-today][data-range-start] > span': {
        borderColor: 'interactive/moderate',
      },

      '& > span': {
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8',
        display: 'flex',
        flex: 1,
      },
    },
  },
  variants: {
    layout: {
      '1-month': {
        content: {
          width: '17.5rem',
        },
      },
      '2-month': {
        content: {
          width: '38.5rem',
        },
      },
    },
    showOutsideDays: {
      false: {
        tableCellTrigger: {
          '&[data-outside-range]': {
            visibility: 'hidden',
          },
        },
      },
      true: {
        tableCellTrigger: {
          '&[data-outside-range]': {
            visibility: 'visible',
          },
        },
      },
    },
    device: {
      mobile: {
        content: {
          boxShadow: 'none',
          paddingBlock: '16',
          paddingInline: '16',
        },
      },
    },
  },
});

export default datePickerSlotRecipe;
